import ExportMenu from "@/components/dashboard/ExportMenu"
import { AnalyticsChart } from "@/components/AnalyticsChart"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { ErrorBoundary } from "@/components/ui/error-boundary"
import useGetDashboard from "@/hooks/api/use-get-dashboard"
import type { FC } from "react"
import { Users, Smartphone, Monitor, Building, Factory, Home, Radio } from "lucide-react"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import { PlatformCombinations } from "./dashboard/PlatformCombinations"
import { SendsTrendAnalysis } from "./dashboard/SendsTrendAnalysis"

const CustomerAnalytics: FC<{
  councilId?: string
}> = (props) => {
  const { data, isLoading, error } = useGetDashboard(Number(props.councilId))

  const exportCustomerData = async (type: string, format: string) => {
    if (!data) {
      console.error("No data available for export")
      return
    }

    const exportData = {
      councilInfo: data.councilInfo,
      stats: data.stats,
      customerCategories: data.customerCategories,
      platformUsage: data.userPlatformData,
      exportDate: new Date().toISOString(),
      exportType: type,
    }

    let content: string
    let mimeType: string
    let filename: string

    try {
      switch (format) {
        case "csv": {
          content = "Category,Type,Value,Description\n"

          // Council Info
          if (exportData.councilInfo) {
            content += `"${exportData.councilInfo.name || "N/A"}",Council Name,"${exportData.councilInfo.name || ""}","Council Information"\n`
          }

          // Statistics
          if (exportData.stats) {
            content += `${exportData.stats.customers || 0},Statistics,Total Customers,"Total number of customers"\n`
            content += `${exportData.stats.activeDevices || 0},Statistics,Active Devices,"Number of active devices"\n`
            content += `${exportData.stats.waterConsumption || 0},Statistics,Water Consumption,"Total water consumption (L)"\n`
            content += `"${exportData.stats.waterEfficiency || "N/A"}",Statistics,Water Efficiency,"Efficiency rating"\n`
            content += `${exportData.stats.activeAlarms || 0},Statistics,Active Alarms,"Number of active alarms"\n`
            content += `${exportData.stats.alertDeviceCount || 0},Statistics,Alert Devices,"Number of devices with active alarms"\n`
            content += `${exportData.stats.leakAlarms || 0},Statistics,Leak Alarms,"Number of leak alarms"\n`
          }

          // Customer Categories
          if (exportData.customerCategories) {
            content += `${exportData.customerCategories.residential || 0},Customer Category,Residential,"Households and apartments"\n`
            content += `${exportData.customerCategories.commercial || 0},Customer Category,Commercial,"Businesses and offices"\n`
            content += `${exportData.customerCategories.industrial || 0},Customer Category,Industrial,"Manufacturing and industry"\n`
          }

          // Platform Usage
          if (exportData.platformUsage && exportData.platformUsage.length > 0) {
            exportData.platformUsage.forEach((platform: { platform?: string; count?: number }) => {
              content += `${platform.count || 0},Platform Usage,${platform.platform || "Unknown"},"App platform usage"\n`
            })
          }

          mimeType = "text/csv;charset=utf-8;"
          filename = `customer-analytics-${exportData.councilInfo?.name?.toLowerCase().replace(/\s+/g, "-") || "export"}.csv`
          break
        }

        case "pdf": {
          const pdf = new jsPDF()
          const pageWidth = pdf.internal.pageSize.getWidth()
          const pageHeight = pdf.internal.pageSize.getHeight()
          let yPosition = 20

          // Title
          pdf.setFontSize(20)
          pdf.text("Customer Analytics Report", pageWidth / 2, yPosition, {
            align: "center",
          })
          yPosition += 15

          // Council Info
          pdf.setFontSize(14)
          pdf.text(`Council: ${exportData.councilInfo?.name || "N/A"}`, 20, yPosition)
          yPosition += 10
          pdf.setFontSize(10)
          pdf.text(`Export Date: ${new Date().toLocaleDateString()}`, 20, yPosition)
          yPosition += 20

          // Statistics Section
          pdf.setFontSize(14)
          pdf.text("Statistics Summary:", 20, yPosition)
          yPosition += 10

          pdf.setFontSize(10)
          if (exportData.stats) {
            pdf.text(`Total Customers: ${exportData.stats.customers || 0}`, 30, yPosition)
            yPosition += 8
            pdf.text(`Active Devices: ${exportData.stats.activeDevices || 0}`, 30, yPosition)
            yPosition += 8
            pdf.text(
              `Water Consumption: ${exportData.stats.waterConsumption || 0} L`,
              30,
              yPosition,
            )
            yPosition += 8
            pdf.text(
              `Water Efficiency: ${exportData.stats.waterEfficiency || "N/A"}`,
              30,
              yPosition,
            )
            yPosition += 8
            pdf.text(`Active Alarms: ${exportData.stats.activeAlarms || 0}`, 30, yPosition)
            yPosition += 8
            pdf.text(`Alert Devices: ${exportData.stats.alertDeviceCount || 0}`, 30, yPosition)
            yPosition += 8
            pdf.text(`Leak Alarms: ${exportData.stats.leakAlarms || 0}`, 30, yPosition)
            yPosition += 15
          }

          // Customer Categories
          if (exportData.customerCategories && yPosition < pageHeight - 60) {
            pdf.setFontSize(14)
            pdf.text("Customer Categories:", 20, yPosition)
            yPosition += 10

            pdf.setFontSize(10)
            pdf.text(
              `Residential: ${exportData.customerCategories.residential || 0}`,
              30,
              yPosition,
            )
            yPosition += 8
            pdf.text(`Commercial: ${exportData.customerCategories.commercial || 0}`, 30, yPosition)
            yPosition += 8
            pdf.text(`Industrial: ${exportData.customerCategories.industrial || 0}`, 30, yPosition)
            yPosition += 15
          }

          // Platform Usage
          if (
            exportData.platformUsage &&
            exportData.platformUsage.length > 0 &&
            yPosition < pageHeight - 40
          ) {
            pdf.setFontSize(14)
            pdf.text("Platform Usage:", 20, yPosition)
            yPosition += 10

            pdf.setFontSize(10)
            exportData.platformUsage.forEach((platform: { platform?: string; count?: number }) => {
              pdf.text(
                `${platform.platform || "Unknown"}: ${platform.count || 0} users`,
                30,
                yPosition,
              )
              yPosition += 8
            })
          }

          // Convert to blob and download
          const pdfBlob = pdf.output("blob")
          const url = URL.createObjectURL(pdfBlob)
          const link = document.createElement("a")
          link.href = url
          link.download = `customer-analytics-${exportData.councilInfo?.name?.toLowerCase().replace(/\s+/g, "-") || "export"}.pdf`
          link.click()
          URL.revokeObjectURL(url)
          return
        }

        case "png": {
          // Find the analytics container to capture
          const analyticsElement = document.querySelector(
            "[data-analytics-container]",
          ) as HTMLElement

          if (!analyticsElement) {
            console.error("Analytics container not found for PNG export")
            return
          }

          try {
            const canvas = await html2canvas(analyticsElement, {
              backgroundColor: "#ffffff",
              scale: 2,
              useCORS: true,
              allowTaint: true,
            })

            const imgData = canvas.toDataURL("image/png")
            const link = document.createElement("a")
            link.href = imgData
            link.download = `customer-analytics-${exportData.councilInfo?.name?.toLowerCase().replace(/\s+/g, "-") || "export"}.png`
            link.click()
          } catch (error) {
            console.error("PNG export failed:", error)
          }
          return
        }

        default: {
          content = JSON.stringify(exportData, null, 2)
          mimeType = "application/json;charset=utf-8;"
          filename = `customer-analytics-${exportData.councilInfo?.name?.toLowerCase().replace(/\s+/g, "-") || "export"}.json`
        }
      }

      const blob = new Blob([content], { type: mimeType })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      link.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Export failed:", error)
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold mb-2">Customer Analytics</h2>
            <p className="text-sm text-muted-foreground">
              Platform usage and customer segmentation data
            </p>
          </div>
          <ExportMenu
            type="customer-analytics"
            label="Export Customer Data"
            onExport={exportCustomerData}
          />
        </div>
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <p className="text-muted-foreground">Failed to load customer analytics data</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-4" data-analytics-container>
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold mb-2">Customer Analytics</h2>
          <p className="text-sm text-muted-foreground">
            Platform usage and customer segmentation data
          </p>
        </div>
        <ExportMenu
          type="customer-analytics"
          label="Export Customer Data"
          onExport={exportCustomerData}
        />
      </div>

      {/* Customer Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Customers</CardTitle>
            <Users className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">
                {typeof data?.stats?.customers === "number" ? data.stats.customers : 0}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Devices</CardTitle>
            <Smartphone className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">
                {typeof data?.stats?.activeDevices === "number" ? data.stats.activeDevices : 0}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Alert Devices</CardTitle>
            <Radio className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">
                {typeof data?.stats?.alertDeviceCount === "number"
                  ? data.stats.alertDeviceCount
                  : 0}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Water Consumption</CardTitle>
            <Monitor className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-20" />
            ) : (
              <div className="text-2xl font-bold">
                {typeof data?.stats?.waterConsumption === "number"
                  ? data.stats.waterConsumption
                  : 0}{" "}
                L
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Water Savings ($)</CardTitle>
            <Building className="size-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold">N/A</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sends Trend Analysis */}
      <ErrorBoundary>
        <SendsTrendAnalysis
          {...(data?.councilInfo?.id ? { councilId: data.councilInfo.id } : {})}
        />
      </ErrorBoundary>

      {/* Customer Category Details */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Residential</CardTitle>
            <Home className="size-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-blue-600">
                {typeof data?.customerCategories?.residential === "number"
                  ? data.customerCategories.residential
                  : 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Households and apartments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Commercial</CardTitle>
            <Building className="size-4 text-green-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {typeof data?.customerCategories?.commercial === "number"
                  ? data.customerCategories.commercial
                  : 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Businesses and offices</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Industrial</CardTitle>
            <Factory className="size-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <div className="text-2xl font-bold text-orange-600">
                {typeof data?.customerCategories?.industrial === "number"
                  ? data.customerCategories.industrial
                  : 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Manufacturing and industry</p>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Platform Usage Chart */}
        <AnalyticsChart
          data={
            data?.userPlatformData?.map((platform) => ({
              platform: platform.platform || "Unknown",
              count: platform.count || 0,
            })) || []
          }
          title="Customer App Usage"
          description="Platform distribution across customers"
        />
        <PlatformCombinations councilId={props.councilId} />
      </div>
    </div>
  )
}

export default CustomerAnalytics
